import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import { getIconComponent } from "@storytell/bsky-icons";
import type { Asset, Concept, Label, View } from "@storytell/types";
import { TbApps, TbCheck, TbChevronRight, TbFileText, TbLoader2, TbStack } from "solid-icons/tb";
import { For, Match, Show, Switch, createEffect, createSignal, on, onCleanup } from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { isLightColor } from "../../helpers/isLightColor";
import type { useMentionsState, MentionItem } from "./useMentionsState";

export const MentionsPickerPopup = (props: {
  mentionsState: ReturnType<typeof useMentionsState>;
}) => {
  const state = props.mentionsState.mentionsState;
  let el!: HTMLDivElement;

  // State for tracking expanded sections
  const [expandedSections, setExpandedSections] = createSignal({
    labels: false,
    concepts: false,
    assets: false,
    views: false,
  });

  const ITEMS_LIMIT = 3;

  // This assumes that assets and concepts are grouped by type, not interleaved
  const assets = () => state.data.filter((d) => d.type === "asset");
  const assetsStart = () => state.data.findIndex((d) => d.type === "asset");
  const concepts = () => state.data.filter((d) => d.type === "concept");
  const conceptsStart = () => state.data.findIndex((d) => d.type === "concept");

  const labels = () =>
    state.data
      .filter((d) => d.type === "label")
      .sort((a, b) => {
        const labelA = a.data as Label;
        const labelB = b.data as Label;
        return labelA.name.localeCompare(labelB.name);
      });
  const labelsStart = () => state.data.findIndex((d) => d.type === "label");

  const views = () => state.data.filter((d) => d.type === "view");
  const viewsStart = () => state.data.findIndex((d) => d.type === "view");

  // Helper functions for slicing items
  const getDisplayedLabels = () => {
    const allLabels = labels();
    return expandedSections().labels ? allLabels : allLabels.slice(0, ITEMS_LIMIT);
  };

  const getDisplayedConcepts = () => {
    const allConcepts = concepts();
    return expandedSections().concepts ? allConcepts : allConcepts.slice(0, ITEMS_LIMIT);
  };

  const getDisplayedAssets = () => {
    const allAssets = assets();
    return expandedSections().assets ? allAssets : allAssets.slice(0, ITEMS_LIMIT);
  };

  const getDisplayedViews = () => {
    const allViews = views();
    return expandedSections().views ? allViews : allViews.slice(0, ITEMS_LIMIT);
  };

  const shouldShowExpandButton = (type: "labels" | "concepts" | "assets" | "views") => {
    switch (type) {
      case "labels":
        return labels().length > ITEMS_LIMIT && !expandedSections().labels;
      case "concepts":
        return concepts().length > ITEMS_LIMIT && !expandedSections().concepts;
      case "assets":
        return assets().length > ITEMS_LIMIT && !expandedSections().assets;
      case "views":
        return views().length > ITEMS_LIMIT && !expandedSections().views;
    }
  };

  const toggleSection = (type: "labels" | "concepts" | "assets" | "views") => {
    setExpandedSections((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Create a flattened navigation structure that includes both items and expand buttons
  type NavigationItem =
    | { type: "item"; data: MentionItem; originalIndex: number }
    | { type: "expand"; section: "labels" | "concepts" | "assets" | "views" };

  const getNavigationItems = (): NavigationItem[] => {
    const items: NavigationItem[] = [];

    // Add views first
    if (viewsStart() !== -1) {
      const displayedViews = getDisplayedViews();
      displayedViews.forEach((view, index) => {
        items.push({
          type: "item",
          data: view,
          originalIndex: viewsStart() + index,
        });
      });
      if (shouldShowExpandButton("views")) {
        items.push({ type: "expand", section: "views" });
      }
    }

    // Add labels
    if (labelsStart() !== -1) {
      const displayedLabels = getDisplayedLabels();
      displayedLabels.forEach((label, index) => {
        items.push({
          type: "item",
          data: label,
          originalIndex: labelsStart() + index,
        });
      });
      if (shouldShowExpandButton("labels")) {
        items.push({ type: "expand", section: "labels" });
      }
    }

    // Add concepts
    if (conceptsStart() !== -1) {
      const displayedConcepts = getDisplayedConcepts();
      displayedConcepts.forEach((concept, index) => {
        items.push({
          type: "item",
          data: concept,
          originalIndex: conceptsStart() + index,
        });
      });
      if (shouldShowExpandButton("concepts")) {
        items.push({ type: "expand", section: "concepts" });
      }
    }

    // Add assets
    if (assetsStart() !== -1) {
      const displayedAssets = getDisplayedAssets();
      displayedAssets.forEach((asset, index) => {
        items.push({
          type: "item",
          data: asset,
          originalIndex: assetsStart() + index,
        });
      });
      if (shouldShowExpandButton("assets")) {
        items.push({ type: "expand", section: "assets" });
      }
    }

    return items;
  };

  const navigationItems = () => getNavigationItems();

  // Helper function to get navigation index from original data index
  const getNavigationIndexFromOriginalIndex = (originalIndex: number): number => {
    const items = navigationItems();
    return items.findIndex((item) => item.type === "item" && item.originalIndex === originalIndex);
  };

  // Helper function to get the active navigation index based on state.activeIndex
  const getActiveNavigationIndex = (): number => {
    if (state.activeIndex < -1000) {
      // This is an expand button index
      return Math.abs(state.activeIndex + 1000);
    }
    return getNavigationIndexFromOriginalIndex(state.activeIndex);
  };

  // Helper function to check if an expand button is active
  const isExpandButtonActive = (navIndex: number): boolean => {
    return state.activeIndex === -1000 - navIndex;
  };

  const updatePosition = (referenceEl: HTMLElement, floatingEl: HTMLElement) => {
    computePosition(referenceEl, floatingEl, {
      placement: "right-end",
      middleware: [offset(20), flip(), shift()],
    }).then((c) => {
      Object.assign(el.style, {
        left: `${c.x}px`,
        top: `${c.y}px`,
      });
    });
  };
  createEffect(
    on(
      () => state.domAnchor,
      (domAnchor) => {
        if (!domAnchor) return;
        const cleanup = autoUpdate(domAnchor, el, () => updatePosition(domAnchor, el));
        onCleanup(() => cleanup());
      },
    ),
  );

  // Reset expanded sections when popup closes
  createEffect(() => {
    if (!state.open) {
      setExpandedSections({
        labels: false,
        concepts: false,
        assets: false,
        views: false,
      });
    }
  });

  // Override keyboard navigation to work with our flattened structure
  createEffect(() => {
    if (!state.open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!state.open) return;

      const navItems = navigationItems();
      if (navItems.length === 0) return;

      const currentNavIndex = getActiveNavigationIndex();

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        props.mentionsState.setLastInteraction("keyboard");

        const direction = e.key === "ArrowDown" ? 1 : -1;
        let newNavIndex = currentNavIndex + direction;

        // Wrap around
        if (newNavIndex >= navItems.length) newNavIndex = 0;
        if (newNavIndex < 0) newNavIndex = navItems.length - 1;

        const newNavItem = navItems[newNavIndex];
        if (newNavItem?.type === "item") {
          props.mentionsState.setActiveIndex(newNavItem.originalIndex);
        } else if (newNavItem?.type === "expand") {
          // For expand buttons, we need to handle them specially
          // Set activeIndex to a special value that we can detect
          props.mentionsState.setActiveIndex(-1000 - navItems.findIndex((item) => item === newNavItem));
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        const activeNavItem = navItems[currentNavIndex];
        if (activeNavItem?.type === "expand") {
          toggleSection(activeNavItem.section);
        } else if (activeNavItem?.type === "item") {
          props.mentionsState.select(activeNavItem.originalIndex);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    onCleanup(() => window.removeEventListener("keydown", handleKeyDown, true));
  });

  // Close on outside click, even if overlays intercept
  createEffect(() => {
    if (!state.open) return;
    const handlePointerDown = (e: PointerEvent) => {
      // If el is not mounted yet, or click is inside the popup, do nothing
      if (!el || el.contains(e.target as Node)) return;
      props.mentionsState.dismiss();
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("pointerdown", handlePointerDown, true); // use capture phase
    onCleanup(() => window.removeEventListener("pointerdown", handlePointerDown, true));
  });

  return (
    <Show when={state.open}>
      <Portal mount={document.body}>
        <div class="absolute inset-0" on:click={() => props.mentionsState.dismiss()} />
        <div
          ref={el}
          class="z-20 bg-tile border border-primary-border rounded-lg shadow-lg absolute p-1 max-h-[50vh] overflow-y-auto max-w-[50vw]"
          style="scroll-padding-top: 2rem; scroll-padding-bottom: 1rem;"
          on:mousemove={() => state.lastInteraction !== "mouse" && props.mentionsState.setLastInteraction("mouse")}
        >
          <Switch>
            <Match when={state.loading}>
              <div class="px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56">
                <TbLoader2 class="animate-spin size-4 text-muted-text" />
                <span>Loading</span>
              </div>
            </Match>
            <Match when={state.error}>
              <button
                type="button"
                onClick={() => props.mentionsState.retry()}
                class="min-h-0 px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56"
              >
                <p>
                  There was an error. <span class="font-medium">Retry?</span>
                </p>
                <TbCheck class="size-4 text-muted-text" />
              </button>
            </Match>
            <Match when={state.data.length === 0}>
              <button
                type="button"
                onClick={() => props.mentionsState.dismiss()}
                class="min-h-0 px-2 py-1 bg-panel rounded flex items-center gap-2 min-w-56"
              >
                <p>
                  No results found. <span class="font-medium">Dismiss?</span>
                </p>
                <TbCheck class="size-4 text-muted-text" />
              </button>
            </Match>

            <Match when={state.data.length > 0}>
              <div class="flex flex-col gap-2">
                <For each={navigationItems()}>
                  {(navItem, navIndex) => {
                    if (navItem.type === "item" && navItem.data.type === "label") {
                      const item = navItem;
                      const labelData = item.data.data as Label;
                      const isFirst =
                        navIndex() === 0 ||
                        navigationItems()[navIndex() - 1]?.type === "expand" ||
                        (navigationItems()[navIndex() - 1] as { type: "item"; data: MentionItem })?.data?.type !==
                          "label";
                      return (
                        <>
                          <Show when={isFirst}>
                            <span class="text-muted-text text-sm select-none ml-2 mt-2">Labels</span>
                          </Show>
                          <button
                            on:mouseenter={() =>
                              state.lastInteraction === "mouse" &&
                              props.mentionsState.setActiveIndex(item.originalIndex)
                            }
                            onClick={() => props.mentionsState.select(item.originalIndex)}
                            class="min-h-0 rounded px-2 py-1 min-w-56"
                            style="scroll-margin-top: 2rem; scroll-margin-bottom: 0.5rem;"
                            classList={{
                              "bg-canvas": state.activeIndex === item.originalIndex,
                            }}
                            type="button"
                          >
                            <MentionLabel label={labelData} active={state.activeIndex === item.originalIndex} />
                          </button>
                        </>
                      );
                    }
                    if (navItem.type === "item" && navItem.data.type === "concept") {
                      const item = navItem;
                      const conceptData = item.data.data as Concept;
                      const isFirst =
                        navIndex() === 0 ||
                        navigationItems()[navIndex() - 1]?.type === "expand" ||
                        (navigationItems()[navIndex() - 1] as { type: "item"; data: MentionItem })?.data?.type !==
                          "concept";
                      return (
                        <>
                          <Show when={isFirst}>
                            <span class="text-concept-icon text-sm select-none ml-2 mt-2">Concepts</span>
                          </Show>
                          <button
                            on:mouseenter={() =>
                              state.lastInteraction === "mouse" &&
                              props.mentionsState.setActiveIndex(item.originalIndex)
                            }
                            onClick={() => props.mentionsState.select(item.originalIndex)}
                            class="min-h-0 rounded px-2 py-1 min-w-56"
                            style="scroll-margin-top: 2rem; scroll-margin-bottom: 0.5rem;"
                            classList={{
                              "bg-concept-bg": state.activeIndex === item.originalIndex,
                            }}
                            type="button"
                          >
                            <MentionConcept concept={conceptData} active={state.activeIndex === item.originalIndex} />
                          </button>
                        </>
                      );
                    }
                    if (navItem.type === "item" && navItem.data.type === "asset") {
                      const item = navItem;
                      const assetData = item.data.data as Asset;
                      const isFirst =
                        navIndex() === 0 ||
                        navigationItems()[navIndex() - 1]?.type === "expand" ||
                        (navigationItems()[navIndex() - 1] as { type: "item"; data: MentionItem })?.data?.type !==
                          "asset";
                      return (
                        <>
                          <Show when={isFirst}>
                            <span class="text-asset-icon text-sm select-none ml-2 mt-2">Assets</span>
                          </Show>
                          <button
                            on:mouseenter={() =>
                              state.lastInteraction === "mouse" &&
                              props.mentionsState.setActiveIndex(item.originalIndex)
                            }
                            onClick={() => props.mentionsState.select(item.originalIndex)}
                            class="min-h-0 rounded px-2 py-1 min-w-56"
                            style="scroll-margin-top: 2rem; scroll-margin-bottom: 0.5rem;"
                            classList={{
                              "bg-asset-bg": state.activeIndex === item.originalIndex,
                            }}
                            type="button"
                          >
                            <MentionAsset asset={assetData} active={state.activeIndex === item.originalIndex} />
                          </button>
                        </>
                      );
                    }
                    if (navItem.type === "item" && navItem.data.type === "view") {
                      const item = navItem;
                      const viewData = item.data.data as View;
                      const isFirst =
                        navIndex() === 0 ||
                        navigationItems()[navIndex() - 1]?.type === "expand" ||
                        (navigationItems()[navIndex() - 1] as { type: "item"; data: MentionItem })?.data?.type !==
                          "view";
                      return (
                        <>
                          <Show when={isFirst}>
                            <span class="text-muted-text text-sm select-none ml-2 mt-2">Views</span>
                          </Show>
                          <button
                            on:mouseenter={() =>
                              state.lastInteraction === "mouse" &&
                              props.mentionsState.setActiveIndex(item.originalIndex)
                            }
                            onClick={() => props.mentionsState.select(item.originalIndex)}
                            class="min-h-0 rounded px-2 py-1 min-w-56"
                            style="scroll-margin-top: 2rem; scroll-margin-bottom: 0.5rem;"
                            classList={{
                              "bg-canvas": state.activeIndex === item.originalIndex,
                            }}
                            type="button"
                          >
                            <MentionView view={viewData} active={state.activeIndex === item.originalIndex} />
                          </button>
                        </>
                      );
                    }
                    if (navItem.type === "expand") {
                      const expandItem = navItem as {
                        type: "expand";
                        section: "labels" | "concepts" | "assets" | "views";
                      };
                      const sectionCounts = {
                        labels: labels().length,
                        concepts: concepts().length,
                        assets: assets().length,
                        views: views().length,
                      };
                      let buttonRef!: HTMLButtonElement;

                      createEffect(() => {
                        if (isExpandButtonActive(navIndex())) {
                          buttonRef.scrollIntoView({ behavior: "instant", block: "nearest" });
                        }
                      });

                      return (
                        <button
                          ref={buttonRef}
                          on:mouseenter={() =>
                            state.lastInteraction === "mouse" && props.mentionsState.setActiveIndex(-1000 - navIndex())
                          }
                          onClick={() => toggleSection(expandItem.section)}
                          class="min-h-0 rounded px-2 py-1 min-w-56 text-muted-text hover:bg-canvas transition-colors flex items-center gap-2"
                          style="scroll-margin-top: 2rem; scroll-margin-bottom: 0.5rem;"
                          classList={{
                            "bg-canvas": isExpandButtonActive(navIndex()),
                          }}
                          type="button"
                        >
                          <TbChevronRight class="size-3" />
                          <span class="text-sm">
                            Show all {sectionCounts[expandItem.section]} {expandItem.section}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  }}
                </For>
              </div>
            </Match>
          </Switch>
        </div>
      </Portal>
    </Show>
  );
};

const MentionLabel = (props: {
  label: Label;
  active: boolean;
}) => {
  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });
  return (
    <div ref={ref} class="flex items-center gap-2">
      <span
        class="rounded-full size-6 grid place-content-center bg-[var(--label-color)]"
        style={{
          "--label-color": props.label.color,
          "--label-icon": isLightColor(props.label.color) ? "black" : "white",
        }}
      >
        <Dynamic
          data-active={props.active}
          class="size-3.5 text-[var(--label-icon)] data-[active=true]:scale-hover transition-transform"
          component={getIconComponent(props.label.icon)}
        />
      </span>
      <div class="flex flex-col text-left max-w-full overflow-hidden">
        <p class="font-medium text-label-text text-sm truncate">{props.label.name}</p>
      </div>
    </div>
  );
};

const MentionAsset = (props: {
  asset: Asset;
  active: boolean;
}) => {
  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });

  return (
    <div ref={ref} class="flex items-center gap-2">
      <TbFileText
        data-active={props.active}
        class="size-4 text-asset-icon data-[active=true]:scale-hover transition-transform"
      />
      <div class="flex flex-col text-left max-w-full overflow-hidden">
        <p class="font-medium text-asset-text text-sm truncate">{props.asset.fileName}</p>
        <p class="text-asset-icon text-xs truncate">{props.asset.displayName}</p>
      </div>
    </div>
  );
};

const MentionConcept = (props: {
  concept: Concept;
  active: boolean;
}) => {
  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });

  return (
    <div ref={ref} class="flex items-center gap-2">
      <TbApps
        data-active={props.active}
        class="size-4 text-concept-icon data-[active=true]:scale-hover transition-transform"
      />
      <div class="flex flex-col text-left max-w-full overflow-hidden">
        <p class="font-medium text-concept-text text-sm truncate">{props.concept.name}</p>
        <p class="text-concept-icon max-w-[40vw] text-xs truncate line-clamp-1">{props.concept.description}</p>
      </div>
    </div>
  );
};

const MentionView = (props: {
  view: View;
  active: boolean;
}) => {
  let ref!: HTMLDivElement;
  createEffect(() => {
    if (props.active) {
      ref.scrollIntoView({ behavior: "instant", block: "nearest" });
    }
  });

  return (
    <div ref={ref} class="flex items-center gap-2">
      <TbStack
        data-active={props.active}
        class="size-4 text-concept-icon data-[active=true]:scale-hover transition-transform"
      />
      <div class="flex flex-col text-left max-w-full overflow-hidden">
        <p class="font-medium text-label-text text-sm truncate">{props.view.name}</p>
        <p class="text-muted-text max-w-[40vw] text-xs truncate line-clamp-1">{props.view.description}</p>
      </div>
    </div>
  );
};
